import type { AgentTool, ToolContext } from './tools';

/**
 * Tool synthesis — compile a NEW agent tool from a spec the model authored. The unbounded
 * frontier of self-evolution: the agent invents its own capabilities.
 *
 * SAFETY (this is the load-bearing part). A synthesized tool's `code` is arbitrary JS that
 * runs IN-PROCESS over the agent's filesystem, so before compiling we statically REJECT a
 * denylist of escape vectors (process/require/import/eval/Function/constructor/__proto__/
 * globalThis/Bun/fetch/network/timers/WebAssembly/infinite-loops/…). A compiled tool sees
 * ONLY its `args` and `ctx` (whose fs is the JailedFilesystem — boundary A); combined with
 * the Agent's timeout/tool-call kill-switches this is defensible. Residual risk remains for
 * a determined denylist bypass, so synthesized tools are OPT-IN and should additionally be
 * self-tested in the sandbox (boundary B) before being trusted in a live loop. See mind/08.
 */
export interface ToolSpec {
  name: string;
  description: string;
  parameters: object; // JSON Schema for args
  /** body of `async (args, ctx) => { … return <string> }` — may use args + ctx.fs ONLY. */
  code: string;
}

/** Static safety gate. Returns the matched escape vectors (empty = code is allowed). */
export function validateToolCode(code: string): { ok: boolean; violations: string[] } {
  const DENY: [string, RegExp][] = [
    ['process', /\bprocess\b/], ['require', /\brequire\b/], ['import', /\bimport\b/],
    ['eval', /\beval\b/], ['Function', /\bFunction\b/], ['constructor', /\bconstructor\b/],
    ['__proto__', /__proto__/], ['prototype', /\bprototype\b/], ['globalThis', /\bglobalThis\b/],
    ['global', /\bglobal\b/], ['window', /\bwindow\b/], ['self', /\bself\b/],
    ['Bun', /\bBun\b/], ['Deno', /\bDeno\b/], ['fetch', /\bfetch\b/], ['XMLHttpRequest', /\bXMLHttpRequest\b/],
    ['WebSocket', /\bWebSocket\b/], ['child_process', /\bchild_process\b/], ['node:', /node:/],
    ['setTimeout', /\bsetTimeout\b/], ['setInterval', /\bsetInterval\b/], ['setImmediate', /\bsetImmediate\b/],
    ['Atomics', /\bAtomics\b/], ['SharedArrayBuffer', /\bSharedArrayBuffer\b/], ['WebAssembly', /\bWebAssembly\b/],
    ['Reflect', /\bReflect\b/], ['Proxy', /\bProxy\b/], ['Buffer', /\bBuffer\b/],
    ['while(true)', /while\s*\(\s*true\s*\)/], ['for(;;)', /for\s*\(\s*;\s*;\s*\)/],
    // escape sequences can smuggle a denied identifier past the substring check
    // (e.g. `process` parses as `process`), so forbid them outright.
    ['\\u escape', /\\u/], ['\\x escape', /\\x/],
  ];
  const violations = DENY.filter(([, re]) => re.test(code)).map(([name]) => name);
  return { ok: violations.length === 0, violations };
}

/**
 * Compile a spec into an AgentTool — THROWS if the code fails the static safety gate.
 * The body runs as `async (args, ctx) => { <code> }`; only `args` and `ctx` are in scope.
 */
export function compileSynthesizedTool(spec: ToolSpec): AgentTool {
  const v = validateToolCode(spec.code);
  if (!v.ok) throw new Error(`unsafe synthesized tool '${spec.name}': blocked tokens [${v.violations.join(', ')}]`);
  if (!/^[A-Za-z_]\w{0,39}$/.test(spec.name)) throw new Error(`invalid tool name '${spec.name}'`);
  // Strict mode; only args + ctx are reachable lexically.
  const fn = new Function('args', 'ctx', `"use strict"; return (async (args, ctx) => { ${spec.code} })(args, ctx);`) as (a: any, c: ToolContext) => Promise<unknown>;
  return {
    name: spec.name,
    description: spec.description,
    parameters: spec.parameters ?? { type: 'object', properties: {} },
    async run(args, ctx) {
      const r = await fn(args, ctx);
      return typeof r === 'string' ? r : JSON.stringify(r ?? '');
    },
  };
}
